import { useState } from "react";
import { AccionesUsuarioCabecera, FilaVerComoMovil } from "@/components/admin/AccionesUsuarioCabecera";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Inbox, FileText, CreditCard, FileCheck,
  AlertTriangle, Handshake, Megaphone, BarChart3, Building2, Package,
  LucideIcon, Menu, Send, History, LayoutTemplate, Home, Wrench, Ticket, Landmark, Headset, ScanSearch,
} from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useCanReturnToAdmin } from "@/hooks/useCanReturnToAdmin";
import { usePublicarVerComoCobranza } from "@/contexts/CobranzaImpersonationContext";
import { PortalTrackingProvider } from "@/contexts/PortalTrackingContext";
import { CobranzaImpersonationSelector } from "./CobranzaImpersonationSelector";
import { usePerfilVista } from "@/hooks/usePerfilVista";
import { useCobranzaEffectiveIdentity } from "@/hooks/usePortalPagePermissions";
import { resolvePortalNavRole } from "@/lib/impersonation/page-permissions";
import { PortalSidebar, PORTAL_SIDEBAR_ASIDE_CLS, PORTAL_SIDEBAR_SHEET_CLS } from "@/components/admin/PortalSidebar";
import { TabBarPlano } from "@/components/ui/tab-bar";
import { KpiCardUniforme } from "@/components/ui/kpi-card";

interface NavItem {
  label: string;
  path: string;
  icon: LucideIcon;
}

interface CobranzaSubmenu {
  id: number;
  nombre: string;
  vista_front_end: string;
  orden: number;
}

/** Menú "Portal de cobranza" en `menus`: por id (igual en dev y prod), nunca por nombre. */
const PORTAL_COBRANZA_MENU_ID = 19;

// Íconos por ruta: renombrar un submenú en el admin no le cambia el ícono.
const RUTA_AVISOS = "/admin/portal-cobranza/comunicacion/avisos";
const RUTA_PLANTILLAS = "/admin/portal-cobranza/comunicacion/plantillas";
const RUTA_ESTADOS_CUENTA = "/admin/portal-cobranza/estados-cuenta";
const RUTA_ATENCION = "/admin/portal-cobranza/atencion-clientes";
const RUTA_REPORTES = "/admin/portal-cobranza/reportes";
const RUTA_VALIDADOR = "/admin/portal-cobranza/validador-movimientos";

const NAV_ICONS_POR_RUTA: Record<string, LucideIcon> = {
  "/admin/portal-cobranza/inmuebles": Building2,
  "/admin/portal-cobranza/productos": Package,
  "/admin/portal-cobranza/mantenimiento": Wrench,
  "/admin/portal-cobranza/unidades": Home,
  "/admin/portal-cobranza/tickets": Ticket,
  "/admin/portal-cobranza/cuentas-cobranza": Inbox,
  "/admin/portal-cobranza/relacion-pagos": CreditCard,
  "/admin/portal-cobranza/promesas-pago": Handshake,
  "/admin/portal-cobranza/conciliaciones": AlertTriangle,
  [RUTA_REPORTES]: BarChart3,
  [RUTA_PLANTILLAS]: LayoutTemplate,
  [RUTA_AVISOS]: Megaphone,
  [RUTA_ESTADOS_CUENTA]: Landmark,
  [RUTA_VALIDADOR]: ScanSearch,
  [RUTA_ATENCION]: Headset,
};

// Respaldo por nombre (mayúscula solo al inicio, como en BD) para una ruta nueva que aún no
// esté en el mapa anterior.
const NAV_ICONS: Record<string, LucideIcon> = {
  "Inmuebles": Building2,
  "Unidades": Home,
  "Productos": Package,
  "Mantenimiento": Wrench,
  "Complementos": Package,
  "Inicio": LayoutDashboard,
  "Cuentas de cobranza": Inbox,
  "Atención a clientes": Headset,
  "Relación de pagos": CreditCard,
  "CEPs pendientes": FileCheck,
  "Conciliaciones": AlertTriangle,
  "Promesas de pago": Handshake,
  "Reportes": BarChart3,
  "Administrar avisos": Megaphone,
  "Enviar avisos": Send,
  "Ejecuciones": History,
  "Plantillas": LayoutTemplate,
};

// Roles que ven todos los submenús activos sin filtrar por submenus_permisos.
// Solo Super Admin ve todos los submenús activos. El rol 2 sale de BD como cualquier otro: con
// [1, 2] veía Regularización y Avisos sin tener fila en submenus_permisos (auditoría 2026-10-22).
const SUPER_ADMIN_ROLES = new Set([1]);

// Etiquetas de header para rutas de detalle que no aparecen en el nav.
const DETAIL_SECTION_LABELS: Record<string, string> = {
  "/admin/portal-cobranza/cuentas-cobranza": "Cuenta de cobranza",
};

interface NavGroup {
  label: string;
  items: NavItem[];
}

// Agrupaciones fijas del sidebar. El ORDEN de grupos es fijo; los ÍTEMS dentro
// de cada grupo vienen de BD (orden por `orden`). Submenú no mapeado → "Operación".
const GROUP_ORDER = ["Operación", "Comunicación", "Herramientas"];
const GROUP_BY_NOMBRE: Record<string, string> = {
  "Reportes": "Herramientas",
};
// Grupo por ruta (manda sobre el nombre): renombrar el submenú en el admin no lo cambia de grupo.
const GROUP_BY_RUTA: Record<string, string> = {
  [RUTA_ESTADOS_CUENTA]: "Herramientas",
  [RUTA_VALIDADOR]: "Herramientas",
  [RUTA_REPORTES]: "Herramientas",
};

// Construye los grupos del sidebar desde los submenús de BD.
// Los submenús bajo /comunicacion/ van al grupo "Comunicación" como ítems planos, igual que el
// resto de los portales. Antes se anidaban en un desplegable "Comunicación" dentro del grupo
// "Comunicación": el rótulo salía dos veces, el padre y el hijo activos se resaltaban a la vez y
// con un hijo activo el desplegable no se podía cerrar.
const buildNavGroups = (subs: CobranzaSubmenu[]): NavGroup[] => {
  const byGroup = new Map<string, NavItem[]>();
  const push = (group: string, item: NavItem) => {
    if (!byGroup.has(group)) byGroup.set(group, []);
    byGroup.get(group)!.push(item);
  };
  for (const s of subs) {
    const group = s.vista_front_end.includes("/comunicacion/")
      ? "Comunicación"
      : GROUP_BY_RUTA[s.vista_front_end] || GROUP_BY_NOMBRE[s.nombre] || "Operación";
    push(group, { label: s.nombre, path: s.vista_front_end, icon: NAV_ICONS_POR_RUTA[s.vista_front_end] || NAV_ICONS[s.nombre] || FileText });
  }
  const ordered = [...GROUP_ORDER, ...[...byGroup.keys()].filter((g) => !GROUP_ORDER.includes(g))];
  return ordered.filter((g) => byGroup.has(g)).map((g) => ({ label: g, items: byGroup.get(g)! }));
};

export const PortalCobranzaLayout = () => {
  // Solo mientras este layout está montado se ve como el usuario (y en solo lectura).
  usePublicarVerComoCobranza();
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const identity = useCobranzaEffectiveIdentity();
  const canImpersonate = profile?.puede_impersonar === true;
  const { canReturnToAdmin } = useCanReturnToAdmin();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleNavigate = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const { data: personaName } = useQuery({
    queryKey: ["cobranza-persona-name", profile?.id_persona],
    queryFn: async () => {
      if (!profile?.id_persona) return null;
      const { data } = await (supabase as any)
        .from("personas")
        .select("nombre_comercial, nombre_legal")
        .eq("id", profile.id_persona)
        .single();
      return data?.nombre_comercial || data?.nombre_legal || null;
    },
    enabled: !!profile?.id_persona,
  });

  // Rol con el que se pinta el menú: el de la sesión, salvo al impersonar, donde
  // siempre es el del impersonado (no hay "Vista completa"). Los roles de
  // SUPER_ADMIN_ROLES ven todos los submenús activos sin filtrar por permisos. Solo cambia lo que se PINTA: la autorización de rutas
  // (PermissionRoute) sigue con el rol de la sesión.
  const { rolId: effectiveRoleId, showAll } = resolvePortalNavRole(identity, SUPER_ADMIN_ROLES);

  // Navegación 100% desde BD (tabla submenus del menú "Portal de cobranza").
  // Sin fallback hardcoded: lo que no esté activo/permitido en BD no aparece,
  // y el path usado es el vista_front_end de BD (revela rutas desincronizadas).
  const { data: navGroups = [], isLoading: navLoading } = useQuery({
    queryKey: ["cobranza-nav", effectiveRoleId, showAll],
    queryFn: async () => {
      const { data: menu } = await (supabase as any)
        .from("menus")
        .select("id")
        .eq("id", PORTAL_COBRANZA_MENU_ID)
        .eq("activo", true)
        .maybeSingle();
      if (!menu) return [];
      const { data: subs } = await (supabase as any)
        .from("submenus")
        .select("id, nombre, vista_front_end, orden")
        .eq("menu_id", menu.id)
        .eq("activo", true)
        .order("orden");
      if (!subs || subs.length === 0) return [];
      let allowed: Set<number> | null = null;
      if (!showAll) {
        if (!effectiveRoleId) return [];
        const { data: perms } = await (supabase as any)
          .from("submenus_permisos")
          .select("submenu_id")
          .in("submenu_id", subs.map((s: any) => s.id))
          .eq("rol_id", effectiveRoleId)
          .eq("activo", true);
        allowed = new Set((perms || []).map((p: any) => p.submenu_id));
      }
      const visible = (subs as CobranzaSubmenu[]).filter(
        (s) => s.vista_front_end && (!allowed || allowed.has(s.id))
      );
      // Plantillas de cobranza quedó dentro de Avisos (plantilla base + avisos internos): quien ve
      // Avisos ya no la ve aparte. El submenú sigue en BD porque de él cuelgan permisos (policy de
      // notificaciones_configuracion) y lo usan los roles que aún no tienen Avisos.
      const conAvisos = visible.some((s) => s.vista_front_end === RUTA_AVISOS);
      return buildNavGroups(conAvisos ? visible.filter((s) => s.vista_front_end !== RUTA_PLANTILLAS) : visible);
    },
    enabled: showAll || !!effectiveRoleId,
  });

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + "/");

  const sectionFromNav = navGroups
    .flatMap((g) => g.items)
    .map((i) => ({ label: i.label, path: i.path }))
    .find((c) => isActive(c.path))?.label;
  const sectionFromDetail = Object.entries(DETAIL_SECTION_LABELS).find(([p]) => isActive(p))?.[1];
  const currentSection = sectionFromNav || sectionFromDetail || "Cobranza";

  // Perfil: con "Ver como", la persona impersonada (nombre y rol suyos), no el admin.
  const perfil = usePerfilVista({
    nombre: personaName || profile?.nombre || profile?.email?.split("@")[0],
    rol: profile?.rol_nombre ?? "Cobranza",
  });
  const userName = perfil.nombre.split(/\s+/).slice(0, 2).join(" ");
  const userRole = perfil.rol || "Cobranza";
  const initials = perfil.iniciales;

  const sidebarContent = (
    <PortalSidebar
      portal="Portal Cobranza"
      miCuenta
      cargando={navLoading}
      groups={navGroups.map((group) => ({
        label: group.label,
        items: group.items.map((item) => ({
          key: item.path,
          label: item.label,
          icon: item.icon,
          active: isActive(item.path),
          onSelect: () => handleNavigate(item.path),
        })),
      }))}
      usuario={{ nombre: userName, rol: userRole, iniciales: initials }}
      onRegresar={canReturnToAdmin ? () => handleNavigate("/admin") : undefined}
      onCerrarSesion={signOut}
    />
  );

  return (
    <PortalTrackingProvider portal="cobranza">
      <div className="min-h-screen flex antialiased">
        {/* Desktop sidebar */}
        <aside className={PORTAL_SIDEBAR_ASIDE_CLS}>
          {sidebarContent}
        </aside>

        {/* Mobile drawer */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="left" className={PORTAL_SIDEBAR_SHEET_CLS}>
            {sidebarContent}
          </SheetContent>
        </Sheet>

        <div className="flex-1 lg:pl-64 min-w-0">
          {/* Desktop header */}
          <header className="hidden lg:flex items-center justify-between gap-3 sticky top-0 z-20 bg-card/95 backdrop-blur-sm border-b border-border-soft px-6 h-14 shadow-[0_1px_3px_0_rgba(0,0,0,0.06)]">
            {/* Section title */}
            <h1 className="min-w-0 flex-1 truncate text-xl font-bold text-foreground tracking-tight">{currentSection}</h1>

            {/* Actions */}
            <AccionesUsuarioCabecera verComo={canImpersonate ? <CobranzaImpersonationSelector /> : undefined} />
          </header>

          {/* Mobile header */}
          <header className="flex lg:hidden flex-col sticky top-0 z-20 bg-card/95 backdrop-blur-sm border-b border-border shadow-[0_1px_3px_0_rgba(0,0,0,0.06)]">
            <div className="flex items-center justify-between px-3 h-14">
            <div className="flex items-center gap-2 min-w-0">
              <button
                onClick={() => setMobileOpen(true)}
                className="p-1.5 -ml-1 rounded-lg text-foreground hover:bg-muted transition-colors"
                aria-label="Abrir menú"
              >
                <Menu className="h-5 w-5" />
              </button>
              <p className="text-[16px] font-bold text-foreground tracking-tight truncate">{currentSection}</p>
            </div>
            <AccionesUsuarioCabecera compacto />
            </div>
            {/* "Ver como" en móvil: segunda fila, igual que el Portal Agente. */}
            {canImpersonate && (
              <FilaVerComoMovil>
                <CobranzaImpersonationSelector />
              </FilaVerComoMovil>
            )}
          </header>


          <main className="min-w-0 overflow-x-clip px-4 py-5 sm:px-8 bg-background min-h-[calc(100vh-56px)]">
            {/* Pestañas de todo el portal: solo texto, sin íconos ni tooltips. Tarjetas KPI de
                todo el portal: un solo tamaño (mismo padding, alto y tipografía). */}
            <TabBarPlano>
              <KpiCardUniforme>
                <Outlet />
              </KpiCardUniforme>
            </TabBarPlano>
          </main>
        </div>
      </div>
    </PortalTrackingProvider>
  );
};
